import {
  formatDateWithOrdinal as dateFormatWithOrdinal,
  formatDateForTooltip,
} from '../../utils/dateFormat';
import { executeChartDownload } from '../../utils/chartDownload';
  import ReactECharts from 'echarts-for-react';
  import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
  import styles from '../../styles/BarChart.module.css';
  
  const CHART_PALETTE_VARS = [
    'var(--chart-palette-1)',
    'var(--chart-palette-2)',
    'var(--chart-palette-3)',
    'var(--chart-palette-4)',
    'var(--chart-palette-5)',
    'var(--chart-palette-6)',
    'var(--chart-palette-7)',
    'var(--chart-palette-8)',
  ];
  
  const CHART_PALETTE_TOKEN_KEYS = [
    '--chart-palette-1',
    '--chart-palette-2',
    '--chart-palette-3',
    '--chart-palette-4',
    '--chart-palette-5',
    '--chart-palette-6',
    '--chart-palette-7',
    '--chart-palette-8',
  ];
  
  // ✅ ADD THIS helper (place near other helpers like readCssVar)
  const getVar = (name) => {
    if (typeof document === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  };
  
  const CANVAS_SAFE_COLOR_RE =
    /^(#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(|hsla?\()/i;
  
  function subscribeTheme(onStoreChange) {
    window.addEventListener('themeChanged', onStoreChange);
    const mo = new MutationObserver(onStoreChange);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      window.removeEventListener('themeChanged', onStoreChange);
      mo.disconnect();
    };
  }
  
  function getIsDarkModeSnapshot() {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  }
  
  function getServerSnapshot() {
    return false;
  }
  
  function readCssVar(name) {
    if (typeof document === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }
  
  /** Resolve a CSS color for ECharts by letting the browser compute it (handles nested `var()` chains). */
  function resolveColorForCanvas(input) {
    const v = input.trim();
    if (!v || typeof document === 'undefined') return v;
  
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;left:-9999px;top:0;color:${v}`;
    document.documentElement.appendChild(probe);
    const resolved = getComputedStyle(probe).color;
    document.documentElement.removeChild(probe);
  
    if (!resolved || resolved === 'rgba(0, 0, 0, 0)') return v;
    return resolved;
  }
  
  function isCanvasParsableColor(value) {
    return CANVAS_SAFE_COLOR_RE.test(value.trim());
  }
  
  /** Rounds padded max up to a clean axis max with a stable tick interval (fixes e.g. 26.50 ticks when data max ~27). */
  function getNiceYAxisScale(maxPadded) {
    if (!Number.isFinite(maxPadded) || maxPadded <= 0) return { min: 0, max: 5, interval: 1 };
    const min = 0;
    let interval;
    if (maxPadded <= 50) {
      interval = 5;
    } else {
      const rough = maxPadded / 6;
      const exp = Math.floor(Math.log10(rough));
      const pow = 10 ** exp;
      const frac = rough / pow;
      const nf = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
      interval = nf * pow;
    }
    const max = Math.max(interval, Math.ceil(maxPadded / interval) * interval);
    return { min, max, interval };
  }
  
  /** ECharts may pass axis values as strings; coerce and avoid spurious `.00` on whole numbers. */
  function formatBarChartYAxisLabel(raw) {
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(n)) return String(raw);
    if (n >= 1000) {
      const thousands = n / 1000;
      if (Math.abs(thousands - Math.round(thousands)) < 1e-6) return `${Math.round(thousands)}k`;
      const compact = parseFloat(thousands.toFixed(2));
      return `${compact}k`;
    }
    if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
    return String(parseFloat(n.toFixed(2)));
  }
  
  const BarChart = React.memo(
    ({
      data: dataProp,
      xAxisData: xAxisDataProp,
      seriesData: seriesDataProp,
      seriesColors,
      defaultHeight = '400px',
      showLegendInteractions = true,
      ariaLabel = 'Bar chart',
      timeRange,
      onTimeRangeChange: _onTimeRangeChange,
      onBarClick,
      showTimeRangeInTooltip = true,
      tooltipDateFormat = 'default',
      customTooltipFormatter,
      showLegendWithPercentage = false,
      className = '',
      downloadRequest,
      threshold,
      thresholdLabel = 'Threshold',
      thresholdColor = 'var(--text-error-primary)',
      showThreshold = false,
      thresholds = [],
    }) => {
      const isDarkMode = useSyncExternalStore(subscribeTheme, getIsDarkModeSnapshot, getServerSnapshot);
      const [, forceUpdate] = useState({});
      const chartRef = useRef(null);
      const downloadRef = useRef(null);
  
      useEffect(() => {
        const handleThemeChange = () => {
          forceUpdate({});
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
      }, []);
  
      const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange ?? 'Monthly');
  
      useEffect(() => {
        if (!timeRange) return;
        setSelectedTimeRange(timeRange);
      }, [timeRange]);
  
      const formatDateWithOrdinal = useCallback((dateStr) => {
        return dateFormatWithOrdinal(dateStr);
      }, []);
  
      const chartTypography = useMemo(() => {
        void isDarkMode;
        const rootPx = (() => {
          if (typeof document === 'undefined') return 16;
          const fs = getComputedStyle(document.documentElement).fontSize;
          const n = parseFloat(fs);
          return Number.isFinite(n) ? n : 16;
        })();
        const fontFamily = readCssVar('--font-family-sans') || 'Manrope, sans-serif';
        return {
          fontFamily,
          tooltipFontPx: Math.round(0.75 * rootPx),
          axisFontPx: Math.round(0.75 * rootPx),
          legendFontRem: '0.875rem',
          markLabelFontPx: Math.round(0.75 * rootPx),
        };
      }, []);
  
      const tooltipAccentColor = useMemo(() => {
        void isDarkMode;
        const v = readCssVar('--text-success-primary');
        return v || 'inherit';
      }, []);
  
      const emphasisShadowColor = useMemo(() => {
        void isDarkMode;
        const v = readCssVar('--chart-emphasis-shadow');
        return v || 'transparent';
      }, []);
  
      const currentData = useMemo(() => {
        const isFullData =
          dataProp &&
          typeof dataProp === 'object' &&
          !Array.isArray(dataProp) &&
          'xAxisData' in dataProp &&
          'seriesData' in dataProp;
        if (isFullData) {
          const d = dataProp;
          return { xAxisData: d.xAxisData ?? [], seriesData: d.seriesData ?? [] };
        }
        const xAxisData = xAxisDataProp ?? [];
        const seriesData = seriesDataProp ?? (Array.isArray(dataProp) ? [{ name: 'Data', data: dataProp }] : []);
        return { xAxisData, seriesData };
      }, [dataProp, xAxisDataProp, seriesDataProp]);
  
      // const showSkeleton =
      //   !currentData.xAxisData?.length || currentData.seriesData.every((s) => !s.data?.length);
  
      const fileName = `chart-${selectedTimeRange.toLowerCase()}`;
  
      const handleDownload = useCallback(
        (format) => {
          executeChartDownload({
            format,
            fileName,
            isDarkMode,
            containerRef: downloadRef,
            getEchartsInstance: () => chartRef.current?.getEchartsInstance() ?? null,
            xAxisData: currentData.xAxisData,
            seriesData: currentData.seriesData,
          });
        },
        [currentData, isDarkMode, fileName]
      );
  
      useEffect(() => {
        if (!downloadRequest) return;
        handleDownload(downloadRequest.format);
      }, [downloadRequest, handleDownload]);
  
      const handleBarClick = useCallback(
        (params) => {
          if (params.dataIndex !== undefined) {
            const seriesIndex = params.seriesIndex ?? 0;
            const series = currentData.seriesData[seriesIndex];
  
            const xAxisValueRaw = currentData.xAxisData[params.dataIndex];
            const xAxisValue =
              typeof xAxisValueRaw === 'number'
                ? Number(xAxisValueRaw).toFixed(2)
                : String(xAxisValueRaw);
            let clickData;
  
            if (series?.clickableData && series.clickableData[params.dataIndex]) {
              const clickableItem = series.clickableData[params.dataIndex];
              clickData = {
                date: formatDateWithOrdinal(clickableItem.date),
                value: clickableItem.value,
                seriesName: clickableItem.seriesName,
                timeRange: clickableItem.timeRange,
                xAxisValue: clickableItem.xAxisValue ?? xAxisValue,
              };
            } else {
              const rawDate = xAxisValueRaw;
              const value = params.value ?? 0;
              const seriesName = params.seriesName ?? series?.name ?? '';
              const dateStr =
                typeof rawDate === 'number'
                  ? Number(rawDate).toFixed(2)
                  : formatDateWithOrdinal(String(rawDate));
  
              clickData = {
                date: dateStr,
                value,
                seriesName,
                timeRange: selectedTimeRange,
                xAxisValue,
              };
            }
            onBarClick?.(clickData);
          }
        },
        [onBarClick, currentData, selectedTimeRange, formatDateWithOrdinal]
      );
  
      const axisLabelColor = resolveColorForCanvas(
        isDarkMode
          ? 'var(--text-neutral-tertiary)'
          : 'var(--text-neutral-primary)'
      ); // ✅ FIX
      const axisLineColor = resolveColorForCanvas(
        isDarkMode
          ? 'var(--text-neutral-tertiary)'
          : 'var(--text-neutral-secondary)'
      ); // ✅ FIX
  
      const colors = useMemo(() => {
        void isDarkMode;
        if (seriesColors?.length) return seriesColors;
        return CHART_PALETTE_TOKEN_KEYS.map((key) => getVar(key));
      }, [seriesColors]);
  
      /**
       * ECharts canvas needs hex/rgb/hsl. Browsers may resolve theme colors to `lab()`/`oklch()`, which ECharts
       * often fails to paint; fall back to the same palette as `global.css` chart tokens.
       */
      const resolvedColors = useMemo(() => {
        const base = colors.length ? colors : CHART_PALETTE_VARS;
        const tokenFallback = CHART_PALETTE_TOKEN_KEYS.map((key) => {
          const raw = readCssVar(key);
          const resolved = resolveColorForCanvas(raw || `var(${key})`);
          if (resolved && isCanvasParsableColor(resolved)) return resolved;
          if (raw && isCanvasParsableColor(raw)) return raw;
          return '';
        });
        const emergency =
          resolveColorForCanvas('var(--text-info-primary)') ||
          readCssVar('--text-info-primary') ||
          'currentColor';
        return base.map((c, i) => {
          const resolved = resolveColorForCanvas(c);
          if (resolved.includes('var(') || !isCanvasParsableColor(resolved)) {
            return tokenFallback[i % tokenFallback.length] || emergency;
          }
          return resolved;
        });
      }, [colors]);
  
      const emphasisBarColor = useMemo(() => {
        void isDarkMode;
        const resolved = resolveColorForCanvas('var(--text-success-primary)');
        if (!resolved || resolved.includes('var(') || !isCanvasParsableColor(resolved)) {
          const fromButton = resolveColorForCanvas('var(--button-secondary)');
          if (fromButton && isCanvasParsableColor(fromButton)) return fromButton;
          const fromSuccess = resolveColorForCanvas('var(--background-success-bold)');
          if (fromSuccess && isCanvasParsableColor(fromSuccess)) return fromSuccess;
          return 'currentColor';
        }
        return resolved;
      }, []);
  
      const formatTooltip = useCallback(
        (params) => {
          if (customTooltipFormatter) {
            return customTooltipFormatter(params);
          }
  
          const rawDate = params[0]?.axisValue ?? '';
          const rawTrimmed = String(rawDate ?? '').trim();
          let formattedDate;
          // Guard against turning "2026" into a parsed date, or trying to parse month labels.
          if (selectedTimeRange === 'Year' && /^\d{4}$/.test(rawTrimmed)) {
            formattedDate = rawTrimmed;
          } else if (selectedTimeRange === 'Monthly' && /^[A-Za-z]{3,}$/.test(rawTrimmed)) {
            formattedDate = rawTrimmed;
          } else if (/^\d{1,3}$/.test(rawTrimmed)) {
            formattedDate = rawTrimmed;
          } else {
          switch (tooltipDateFormat) {
            case 'ordinal':
              formattedDate = dateFormatWithOrdinal(rawDate);
              break;
            case 'tooltip':
              formattedDate = formatDateForTooltip(rawDate);
              break;
            default:
              formattedDate = formatDateForTooltip(rawDate);
              break;
          }
          }
          const tooltipContent = [];
          if (showTimeRangeInTooltip) {
            tooltipContent.push(
              `<div style="display:flex;justify-content:space-between;align-items:center;margin:0;line-height:var(--line-height-tight);min-width:200px;font-family:${chartTypography.fontFamily};font-size:${chartTypography.tooltipFontPx}px;">
                <span style="color:var(--text-neutral-primary);">${formattedDate}</span>
                <span style="font-weight:var(--font-weight-bold);color:${tooltipAccentColor};">${selectedTimeRange}</span>
              </div>`
            );
          } else {
            tooltipContent.push(
              `<div style="margin:0;line-height:var(--line-height-tight);font-family:${chartTypography.fontFamily};font-size:${chartTypography.tooltipFontPx}px;color:var(--text-neutral-primary);">
                <span>${formattedDate}</span>
              </div>`
            );
          }
          tooltipContent.push(
            ...params.map((param, index) => {
              const seriesColor = param.color || resolvedColors[index % resolvedColors.length];
              return `<div style="margin-top:10px;line-height:var(--line-height-tight);font-family:${chartTypography.fontFamily};font-size:${chartTypography.tooltipFontPx}px;">
                  <span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background-color:${seriesColor};"></span>
                  <span style="font-weight:var(--font-weight-medium);color:var(--text-neutral-primary);">${param.seriesName || ''}</span>
                  <span style="margin-left:8px;color:var(--text-neutral-secondary);">${param.value}</span>
                </div>`;
            })
          );
  
          return tooltipContent.join('');
        },
        [
          selectedTimeRange,
          showTimeRangeInTooltip,
          tooltipDateFormat,
          customTooltipFormatter,
          resolvedColors,
          chartTypography.fontFamily,
          chartTypography.tooltipFontPx,
          tooltipAccentColor,
        ]
      );
  
      const valueAxisMaxPadded = useMemo(() => {
        let maxValue = 0;
        currentData.seriesData.forEach((series) => {
          if (series.data?.length) {
            const nums = series.data.filter((d) => d != null && typeof d === 'number');
            if (nums.length) {
              const seriesMax = Math.max(...nums);
              if (seriesMax > maxValue) maxValue = seriesMax;
            }
          }
        });
        if (threshold !== undefined && showThreshold) {
          maxValue = Math.max(maxValue, threshold);
        }
        if (thresholds?.length) {
          thresholds.forEach((t) => {
            maxValue = Math.max(maxValue, t.value);
          });
        }
        return maxValue > 0 ? maxValue * 1.1 : 1;
      }, [currentData.seriesData, threshold, showThreshold, thresholds]);
  
      const yAxisScale = useMemo(
        () => getNiceYAxisScale(valueAxisMaxPadded),
        [valueAxisMaxPadded]
      );
  
      const thresholdLineFallback = 'var(--text-error-primary)';
  
      const option = useMemo(
        () => ({
          animation: true,
          animationDuration: 1000,
          animationDurationUpdate: 500,
          animationEasing: 'cubicInOut',
          animationEasingUpdate: 'cubicInOut',
          animationThreshold: 2000,
          stateAnimation: {
            duration: 300,
            easing: 'cubicOut',
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: resolveColorForCanvas('var(--background-neutral-primary)'), // ✅ FIX
            borderWidth: 0,
            borderRadius: 8,
            padding: [12, 16],
            textStyle: {
              fontFamily: chartTypography.fontFamily,
              fontSize: chartTypography.tooltipFontPx,
              color: 'var(--text-neutral-primary)',
              lineHeight: chartTypography.tooltipFontPx + 4,
            },
            extraCssText: `font-family:${chartTypography.fontFamily};`,
            formatter: formatTooltip,
          },
          color: resolvedColors,
          legend: {
            // Monthly uses a single series; still show the legend so the series color is visible (matches Weekly/Yearly UX).
            show: showLegendWithPercentage || currentData.seriesData.length > 0,
            data: currentData.seriesData.map((s) => s.name),
            top: 14,
            bottom: 'auto',
            type: 'scroll',
            orient: 'horizontal',
            icon: 'circle',
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 15,
            selectedMode: showLegendInteractions,
            textStyle: {
              fontFamily: chartTypography.fontFamily,
              fontSize: chartTypography.legendFontRem,
              color: 'var(--text-neutral-primary)',
              fontWeight: 400,
            },
            formatter: showLegendWithPercentage
              ? (name) => {
                  const series = currentData.seriesData.find((s) => s.name === name);
                  if (!series || !series.data || series.data.length === 0) return name;
                  const total = series.data.reduce(
                    (sum, val) => sum + (typeof val === 'number' ? val : 0),
                    0
                  );
                  const allSeriesTotal = currentData.seriesData.reduce((sum, s) => {
                    const seriesTotal = s.data.reduce(
                      (acc, v) => acc + (typeof v === 'number' ? v : 0),
                      0
                    );
                    return sum + seriesTotal;
                  }, 0);
  
                  const percentage =
                    allSeriesTotal > 0 ? ((total / allSeriesTotal) * 100).toFixed(1) : '0.0';
                  return `${name}: ${percentage}%`;
                }
              : undefined,
          },
          grid: {
            left: '0%',
            right: '0.1%',
            bottom: '0%',
            top: '14%',
            containLabel: true,
            // Only color the plot area (inside axes), not the whole chart canvas.
            backgroundColor: '#F8FEFA',
          },
          xAxis: {
            type: 'category',
            data: currentData.xAxisData.map((val) =>
              typeof val === 'number' ? Number(val).toFixed(2) : String(val)
            ),
            axisLabel: {
              fontFamily: chartTypography.fontFamily,
              fontSize: chartTypography.axisFontPx,
              color: axisLabelColor,
              rotate: selectedTimeRange === 'Monthly' ? 45 : selectedTimeRange === 'Week' ? 30 : 0,
              margin: 20,
              letterSpacing: '-0.02em',
              formatter: (value) => {
                if (value == null || value === '') return value;
                const s = String(value);
                const n = Number(s);
                if (!Number.isFinite(n)) return s;
                if (Number.isInteger(n)) return s;
                return n.toFixed(2);
              },
            },
            axisTick: { show: false, alignWithLabel: true },
            axisLine: {
              show: false,
              lineStyle: {
                color: axisLineColor,
              },
            },
          },
          yAxis: {
            type: 'value',
            min: yAxisScale.min,
            max: yAxisScale.max,
            interval: yAxisScale.interval,
            axisLabel: {
              formatter: (val) => formatBarChartYAxisLabel(val),
              fontFamily: chartTypography.fontFamily,
              fontSize: chartTypography.axisFontPx,
              color: axisLabelColor,
              letterSpacing: '-0.02em',
              margin: 20,
            },
            axisTick: { show: false },
            axisLine: {
              show: false,
              lineStyle: {
                color: axisLineColor,
              },
            },
            splitLine: {
              show: false,
            },
          },
          series: currentData.seriesData.map((item, index) => {
            const markLineData = [];
            if (index === 0) {
              if (threshold !== undefined && showThreshold) {
                markLineData.push({
                  yAxis: threshold,
                  name: thresholdLabel,
                  lineStyle: { color: thresholdColor, type: 'dashed', width: 2 },
                  label: {
                    formatter: thresholdLabel,
                    position: 'middle',
                    fontSize: chartTypography.markLabelFontPx,
                    color: thresholdColor,
                    fontWeight: 'bold',
                  },
                });
              }
              if (thresholds?.length) {
                thresholds.forEach((t) => {
                  const lineColor = t.color ?? thresholdLineFallback;
                  markLineData.push({
                    yAxis: t.value,
                    name: t.label,
                    lineStyle: { color: lineColor, type: 'dashed', width: 2 },
                    label: {
                      formatter: t.label,
                      position: 'middle',
                      fontSize: chartTypography.markLabelFontPx,
                      color: lineColor,
                      fontWeight: 'bold',
                    },
                  });
                });
              }
            }
            return {
              name: item.name,
              type: 'bar',
              data: item.data || [],
              maxBarWidth: '26px',
              barGap: '10%',
              barCategoryGap: '10%',
              ...(markLineData.length > 0 && {
                markLine: { data: markLineData, symbol: ['none', 'none'] },
              }),
              itemStyle: {
                color: resolvedColors[index % resolvedColors.length],
                borderRadius: 0,
                opacity: isDarkMode ? 0.8 : 1,
              },
              emphasis: {
                focus: 'series',
                itemStyle: {
                  color: emphasisBarColor,
                  opacity: 1,
                  shadowBlur: 10,
                  shadowColor: emphasisShadowColor,
                },
              },
              showBackground: false,
            };
          }),
        }),
        [
          resolvedColors,
          currentData,
          selectedTimeRange,
          showLegendInteractions,
          isDarkMode,
          formatTooltip,
          chartTypography,
          axisLabelColor,
          axisLineColor,
          yAxisScale,
          threshold,
          thresholdLabel,
          thresholdColor,
          showThreshold,
          thresholds,
          showLegendWithPercentage,
          emphasisBarColor,
          emphasisShadowColor,
          thresholdLineFallback,
        ]
      );
  
      /*
      const barChartSkeleton = useMemo(() => {
        const baseHeight =
          typeof defaultHeight === 'number'
            ? defaultHeight
            : parseInt(String(defaultHeight).replace(/px|%|em|rem/, ''), 10) || 400;
        const skeletonHeight = `${Math.max(baseHeight + 80, 480)}px`;
        const barCount = 6;
        const baseHeights = [0.4, 0.65, 0.5, 0.8, 0.35, 0.6];
        const barHeights = baseHeights.slice(0, barCount);
        const gridCols = { gridTemplateColumns: `repeat(${barCount}, 1fr)` };
  
        return (
          <div
            className={styles.skeletonRoot}
            style={{ height: skeletonHeight, minHeight: skeletonHeight }}
            aria-hidden
          >
            <div className={styles.skeletonLegend}>
              <div className={styles.skeletonLegendGroup}>
                <div className={styles.skeletonDot} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                <div className={styles.skeletonDot} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
              </div>
            </div>
            <div className={styles.skeletonMain}>
              <div className={styles.skeletonYAxis}>
                <div className={styles.skeletonYTicks}>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={styles.skeletonYTick}>
                      <div className={styles.skeletonYTickBar} aria-hidden />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.skeletonPlot}>
                <div className={styles.skeletonGrid}>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={styles.skeletonGridLine} />
                  ))}
                </div>
                <div className={styles.skeletonBars} style={gridCols}>
                  {barHeights.map((pct, i) => (
                    <div key={i} className={styles.skeletonBarCol}>
                      <div
                        className={styles.skeletonBar}
                        style={{ height: `${Math.max(24, pct * 100)}%`, minHeight: 24 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.skeletonXAxis}>
              <div className={styles.skeletonXSpacer} aria-hidden />
              <div className={styles.skeletonXLabels} style={gridCols}>
                {Array.from({ length: barCount }).map((_, i) => (
                  <div key={i} className={styles.skeletonXLabel}>
                    <div
                      className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`}
                      aria-hidden
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }, [defaultHeight]);
      */
  
      const chartContent = (
        <div
          className={`${styles.chartOuter} ${className}`.trim()}
          style={{ marginTop: 0, paddingTop: 0 }}
          role="img"
          aria-label={ariaLabel}
        >
          {/* {showSkeleton ? (
            barChartSkeleton
          ) : ( */}
          <ReactECharts
            key={isDarkMode ? 'dark' : 'light'} // ✅ FIX (forces update)
            ref={chartRef}
            option={option}
            notMerge
            lazyUpdate
            className={styles.echartsRoot}
            style={{
              height: typeof defaultHeight === 'number' ? `${defaultHeight}px` : defaultHeight,
              marginTop: 0,
            }}
            opts={{ renderer: 'canvas' }}
            onEvents={{
              click: handleBarClick,
            }}
          />
          {/* )} */}
        </div>
      );
  
      return (
        <>
          <div ref={downloadRef} className={styles.downloadWrap} style={{ marginTop: 0, paddingTop: 0 }}>
            {chartContent}
          </div>
        </>
      );
    }
  );
  
  BarChart.displayName = 'BarChart';
  
  export default BarChart;
  
  
  
  
  